import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { formatRub } from "./catalog";
import type { HistoryPoint } from "./types";

const COLORS = ["#6ee7b7", "#60a5fa", "#f59e0b", "#f472b6", "#a78bfa", "#22d3ee"];
const WIDTH = 720;
const HEIGHT = 330;
const PAD = { left: 54, right: 24, top: 24, bottom: 40 };

interface Series {
  offerId: number;
  label: string;
  color: string;
  points: Array<HistoryPoint & { x: number; y: number }>;
}

export function PriceChart({ points }: { points: HistoryPoint[] }) {
  const scroll = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (scroll.current) scroll.current.scrollLeft = scroll.current.scrollWidth;
    setSelected(points.reduce<HistoryPoint | null>((latest, point) => !latest || new Date(point.checked_at) > new Date(latest.checked_at) ? point : latest, null));
  }, [points]);
  const [selected, setSelected] = useState<HistoryPoint | null>(points.at(-1) ?? null);
  const { series, minTime, maxTime, minPrice, maxPrice } = useMemo(() => {
    const valid = points
      .map((point) => ({ ...point, time: new Date(point.checked_at).getTime() }))
      .filter((point) => Number.isFinite(point.time) && Number.isFinite(point.price_rub));
    const times = valid.map((point) => point.time);
    const prices = valid.map((point) => point.price_rub);
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    const rawMin = Math.min(...prices);
    const rawMax = Math.max(...prices);
    const priceGap = Math.max(rawMax - rawMin, rawMax * 0.1, 1);
    const minPrice = Math.max(0, rawMin - priceGap * 0.15);
    const maxPrice = rawMax + priceGap * 0.15;
    const timeRange = Math.max(maxTime - minTime, 1);
    const priceRange = Math.max(maxPrice - minPrice, 1);
    const grouped = new Map<number, typeof valid>();
    valid.forEach((point) => {
      const current = grouped.get(point.offer_id) ?? [];
      current.push(point);
      grouped.set(point.offer_id, current);
    });
    const series: Series[] = Array.from(grouped.entries()).map(([offerId, rows], index) => ({
      offerId,
      label: rows[0].label,
      color: COLORS[index % COLORS.length],
      points: rows
        .slice()
        .sort((left, right) => left.time - right.time)
        .map((point) => ({
          ...point,
          x: PAD.left + ((point.time - minTime) / timeRange) * (WIDTH - PAD.left - PAD.right),
          y: PAD.top + ((maxPrice - point.price_rub) / priceRange) * (HEIGHT - PAD.top - PAD.bottom),
        })),
    }));
    return { series, minTime, maxTime, minPrice, maxPrice };
  }, [points]);

  if (!points.length || !Number.isFinite(minPrice)) {
    return <div className="empty-chart">История цены пока не накоплена.</div>;
  }

  const yTicks = [0, 1, 2, 3].map((index) => {
    const ratio = index / 3;
    return {
      value: maxPrice - (maxPrice - minPrice) * ratio,
      y: PAD.top + (HEIGHT - PAD.top - PAD.bottom) * ratio,
    };
  });
  const dateLabel = (time: number) =>
    new Intl.DateTimeFormat("ru-RU", { month: "short", year: "2-digit" }).format(time);

  return (
    <div className="chart-shell">
      <div className="chart-selection" aria-live="polite">
        {selected ? (
          <>
            <span>{selected.label}</span>
            <strong>{formatRub(selected.price_rub)}</strong>
            <span>{new Intl.DateTimeFormat("ru-RU", { dateStyle: "medium" }).format(new Date(selected.checked_at))}</span>
          </>
        ) : null}
      </div>
      {selected?.marketplace?.winner ? <p className="offer-meta">
        {selected.marketplace.winner.delivery_type === "gift" ? "Гифт" : "Ключ"}
        {" · "}{selected.marketplace.winner.seller_name}
        {" · товар "}{selected.marketplace.winner.product_id}
        {" · аккаунт KZ · "}{selected.marketplace.winner.selection.map(choice => choice.label).join(" · ") || selected.marketplace.winner.territory}
        {selected.marketplace.coverage.limited ? " · минимум среди проверенных" : ""}
      </p> : null}
      <div className="chart-scroll" ref={scroll}>
        <svg className="price-chart" viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="История цены в рублях">
          {yTicks.map((tick) => (
            <g key={tick.y}>
              <line x1={PAD.left} y1={tick.y} x2={WIDTH - PAD.right} y2={tick.y} className="chart-grid" />
              <text x={PAD.left - 10} y={tick.y + 4} textAnchor="end" className="chart-label">
                {Math.round(tick.value).toLocaleString("ru-RU")}
              </text>
            </g>
          ))}
          <text x={PAD.left} y={HEIGHT - 12} className="chart-label">{dateLabel(minTime)}</text>
          <text x={WIDTH - PAD.right} y={HEIGHT - 12} textAnchor="end" className="chart-label">{dateLabel(maxTime)}</text>
          {series.map((item) => (
            <g key={item.offerId}>
              <polyline
                points={item.points.map((point) => `${point.x},${point.y}`).join(" ")}
                fill="none"
                stroke={item.color}
                strokeWidth="3"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {item.points.map((point, index) => (
                <circle
                  key={`${item.offerId}-${point.checked_at}-${index}`}
                  cx={point.x}
                  cy={point.y}
                  r={selected === point ? 7 : 4.5}
                  fill={item.color}
                  stroke="var(--surface)"
                  strokeWidth="2"
                  tabIndex={0}
                  role="button"
                  aria-label={`${point.label}, ${formatRub(point.price_rub)}`}
                  onClick={() => setSelected(point)}
                  onFocus={() => setSelected(point)}
                  onPointerEnter={() => setSelected(point)}
                />
              ))}
            </g>
          ))}
        </svg>
      </div>
      <div className="chart-legend">
        {series.map((item) => (
          <button key={item.offerId} type="button" onClick={() => setSelected(item.points.at(-1) ?? null)}>
            <span style={{ background: item.color }} />{item.label}
          </button>
        ))}
      </div>
    </div>
  );
}
